import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import SEO from '../components/SEO';
import { usePageView } from '../hooks/usePageView';
import { Facebook, Instagram } from 'lucide-react';
import TrendingPromoSection from '../components/TrendingPromoSection';

const REDIRECT_URL = window.location.origin + '/dashboard';
const AUTH_URL = `${process.env.REACT_APP_AUTH_URL}/?redirect=${encodeURIComponent(REDIRECT_URL)}`;

const Landing = () => {
  usePageView('landing');
  const navigate = useNavigate();
  const [disclaimerOpen, setDisclaimerOpen] = useState(false);
  const [testingOpen, setTestingOpen] = useState(false);
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const [featureModal, setFeatureModal] = useState(null);
  const [showBrowserWarning, setShowBrowserWarning] = useState(false);

  // Detect if user is in an in-app browser (not Safari)
  const isInAppBrowser = () => {
    const ua = navigator.userAgent || navigator.vendor || window.opera;
    
    // Check for iOS in-app browsers
    if (/iPhone|iPad|iPod/.test(ua)) {
      // Check if NOT Safari (in-app browsers have different user agents)
      const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|OPiOS/.test(ua);
      const isInstagram = /Instagram/.test(ua);
      const isFacebook = /FBAN|FBAV/.test(ua);
      const isTwitter = /Twitter/.test(ua);
      const isLinkedIn = /LinkedInApp/.test(ua);
      const isMessenger = /Messenger/.test(ua);
      
      return !isSafari || isInstagram || isFacebook || isTwitter || isLinkedIn || isMessenger;
    }
    return false;
  };

  const handleLogin = () => {
    // Check if in an in-app browser on iOS
    if (isInAppBrowser()) {
      setShowBrowserWarning(true);
      return;
    }
    window.location.href = AUTH_URL;
  };

  const openInSafari = () => {
    // Try to open the current page in Safari
    const currentUrl = window.location.href;
    // iOS will prompt to open in Safari
    window.location.href = `x-safari-${currentUrl.replace('https://', '').replace('http://', '')}`;
  };

  const featureDetails = {
    paddock: {
      title: "Your Paddock - Organise Your Shows",
      description: "Keep track of everything you're watching in one place. Your Paddock is your personal show management system.",
      features: [
        "**Currently Watching:** Shows you're actively following right now",
        "**Waiting for Next Season:** Track shows between seasons so you never miss a premiere",
        "**Completed:** Archive shows you've finished watching",
        "**Want to Watch:** Build your watchlist of shows you plan to start"
      ]
    },
    graze: {
      title: "Graze - AI-Powered Recommendations",
      description: "Discover your next binge-worthy show with personalised AI recommendations based on your taste and what your flock loves.",
      features: [
        "**Smart Analysis:** AI analyzes your shows, ratings, and reviews to understand your taste",
        "**Flock Picks:** See shows your friends rated highly with their average scores",
        "**Hidden Gems:** Discover lesser-known shows that match your preferences",
        "**Streaming Info:** Check which streaming services have each recommendation in your country"
      ]
    },
    episodes: {
      title: "Coming Episodes - Never Miss a Show",
      description: "Stay on top of your favourite shows with upcoming episode information. Know exactly when the next episode drops so you're always ready to watch.",
      features: [
        "**Upcoming List:** See which of your shows have new episodes coming soon",
        "**Release Dates:** Get accurate air dates adjusted to your local timezone",
        "**Country Info:** Know which country each show airs in first",
        "**Quick Access:** Jump straight to the show details from the episode list"
      ]
    },
    rating: {
      title: "Raate Your Shows - Share Your Verdicts",
      description: "Give shows a numeric score from 1-10 and write reviews to let your flock know if a show is worth their time.",
      features: [
        "**Numeric Ratings:** Score shows from 1-10 based on your experience",
        "**Written Reviews:** Share your detailed thoughts and opinions",
        "**Public Sharing:** Your ratings appear in your flock's feed",
        "**Statistics:** See how your ratings compare to your friends and all users"
      ]
    },
    flock: {
      title: "Find Your Flock - Connect with Friends",
      description: "Follow friends to see what they're watching and discover new shows through their recommendations.",
      features: [
        "**Search by Handle:** Find friends using their unique @username",
        "**Follow System:** Follow friends to see their reviews in your feed",
        "**Friend Activity:** Track what your flock is currently watching",
        "**Privacy First:** Only users who know your handle can find you"
      ]
    },
    feed: {
      title: "Feed Your Flock - Social Discovery",
      description: "A social feed where you and your flock share ratings and reviews. Discover what's worth watching through trusted opinions.",
      features: [
        "**Friend Reviews:** See what your flock is rating and reviewing",
        "**Like & Comment:** Engage with reviews from your friends",
        "**Add to Watchlist:** One-click to add shows from feed to your Want to Watch list",
        "**My Posts Tab:** View all your own reviews with likes and comments"
      ]
    }
  };

  return (
    <>
      <SEO 
        title="baad.tv - Track TV Shows & Share Reviews with Friends"
        description="Free social TV tracker! Track shows across Netflix, Disney+, Prime & more. Rate shows, follow friends & get personalised recommendations. Join the flock today!"
        url="/"
        image="/sheep-logo.png"
      />
      <div className="landing-page">
      <div className="landing-content">
        <div className="landing-hero">
          <div className="logo-container">
            <img 
              src="/sheep-logo.png" 
              alt="baad.tv - Social TV Show Tracker Logo" 
              className="landing-logo"
              width="200"
              height="200"
            />
          </div>
          <h1 data-testid="landing-title">baad.tv</h1>
          <p className="tagline">TV so good, its baad.</p>
          <p data-testid="landing-subtitle">The social network for TV lovers. Never miss an episode, share what you're watching with friends, get recommendations based on your favorites, and get personalised recommendations from your flock.</p>
          <Button 
            data-testid="google-login-btn" 
            onClick={handleLogin}
            size="lg"
            className="login-btn"
          >
            <svg className="google-icon" viewBox="0 0 24 24" width="20" height="20">
              <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Join the Flock
          </Button>

        </div>
        
        <div className="features-grid">
          <div className="feature-card clickable-card" onClick={() => setFeatureModal('paddock')}>
            <div className="feature-icon">
              <img 
                src="https://customer-assets.emergentagent.com/job_baadtv-tracker/artifacts/069gpugl_Gate%202.png" 
                alt="Track your TV shows" 
                className="icon-image"
                loading="lazy"
                width="80"
                height="80"
              />
            </div>
            <h3>Your Paddock</h3>
            <p>Organise what you're watching, waiting for, completed, or want to watch</p>
            <div className="click-hint">Click to learn more →</div>
          </div>
          <div className="feature-card clickable-card" onClick={() => setFeatureModal('graze')}>
            <div className="feature-icon">
              <img 
                src="https://customer-assets.emergentagent.com/job_afd8826e-d941-4dc3-bb2d-6d35e1ceb17c/artifacts/32huz0a6_Graze.png" 
                alt="AI-powered show recommendations" 
                className="icon-image"
                loading="lazy"
                width="80"
                height="80"
              />
            </div>
            <h3>Graze</h3>
            <p>AI-powered recommendations based on your taste and what your flock loves</p>
            <div className="click-hint">Click to learn more →</div>
          </div>
          <div className="feature-card clickable-card" onClick={() => setFeatureModal('episodes')}>
            <div className="feature-icon">
              <img 
                src="https://customer-assets.emergentagent.com/job_afd8826e-d941-4dc3-bb2d-6d35e1ceb17c/artifacts/pa71n2uq_Sun.png" 
                alt="Upcoming episodes calendar" 
                className="icon-image"
                loading="lazy"
                width="80"
                height="80"
              />
            </div>
            <h3>Coming Episodes</h3>
            <p>Never miss a show - see exactly when your next episodes are airing</p>
            <div className="click-hint">Click to learn more →</div>
          </div>
          <div className="feature-card clickable-card" onClick={() => setFeatureModal('rating')}>
            <div className="feature-icon">
              <img 
                src="/ribbon-icon.png" 
                alt="Share reviews and recommendations" 
                className="icon-image"
                loading="lazy"
                width="80"
                height="80"
              />
            </div>
            <h3>Raate Your Shows</h3>
            <p>Score shows 1-10 and let the flock know which ones are up to muster</p>
            <div className="click-hint">Click to learn more →</div>
          </div>
          <div className="feature-card clickable-card" onClick={() => setFeatureModal('flock')}>
            <div className="feature-icon">
              <img 
                src="/flock-icon.png" 
                alt="Connect with friends and TV lovers" 
                className="icon-image"
                loading="lazy"
                width="80"
                height="80"
              />
            </div>
            <h3>Find Your Flock</h3>
            <p>Follow friends and discover what they're binging</p>
            <div className="click-hint">Click to learn more →</div>
          </div>
          <div className="feature-card clickable-card" onClick={() => setFeatureModal('feed')}>
            <div className="feature-icon">
              <img 
                src="/hay-bale-icon.png" 
                alt="Discover what to watch next" 
                className="icon-image"
                loading="lazy"
                width="80"
                height="80"
              />
            </div>
            <h3>Feed your Flock</h3>
            <p>You and your flock share raatings and reviews with each other.</p>
            <div className="click-hint">Click to learn more →</div>
          </div>
        </div>

        {/* Trending promo strip — invites guests + logged-in users to the public /trending page */}
        <TrendingPromoSection />

        {/* Browser Warning Modal for iOS in-app browsers */}
        <Dialog open={showBrowserWarning} onOpenChange={setShowBrowserWarning}>
          <DialogContent className="browser-warning-dialog">
            <DialogHeader>
              <DialogTitle>🔒 Please Use Safari Browser</DialogTitle>
              <DialogDescription asChild>
                <div className="dialog-text" style={{ fontSize: '1rem', lineHeight: '1.6' }}>
                  <p style={{ marginBottom: '1rem' }}>
                    For security reasons, Google requires login through Safari (not in-app browsers).
                  </p>
                  
                  <div style={{ 
                    background: '#1a1a1a', 
                    padding: '1rem', 
                    borderRadius: '8px',
                    border: '1px solid #E62F88',
                    marginBottom: '1rem'
                  }}>
                    <p style={{ fontWeight: '600', marginBottom: '0.5rem', color: '#E62F88' }}>
                      How to open in Safari:
                    </p>
                    <ol style={{ marginLeft: '1.5rem', listStyle: 'decimal' }}>
                      <li style={{ marginBottom: '0.5rem' }}>Tap the <strong>"..."</strong> or <strong>Share</strong> button</li>
                      <li style={{ marginBottom: '0.5rem' }}>Select <strong>"Open in Safari"</strong></li>
                      <li>Then click "Join the Flock" again</li>
                    </ol>
                  </div>

                  <p style={{ fontSize: '0.9rem', color: '#999' }}>
                    This ensures your Google login is secure and compliant with Google's policies.
                  </p>

                  <div style={{ marginTop: '1.5rem', textAlign: 'center' }}>
                    <Button 
                      onClick={() => {
                        // Copy current URL to help user
                        const url = window.location.href;
                        if (navigator.clipboard) {
                          navigator.clipboard.writeText(url);
                        }
                        setShowBrowserWarning(false);
                      }}
                      style={{ background: '#E62F88',
                        color: '#000',
                        fontWeight: '600', borderRadius: '8px' }}
                    >
                      Got it! I'll open in Safari
                    </Button>
                  </div>
                </div>
              </DialogDescription>
            </DialogHeader>
          </DialogContent>
        </Dialog>

        {/* Feature Detail Modal */}
        {featureModal && featureDetails[featureModal] && (
          <Dialog open={!!featureModal} onOpenChange={() => setFeatureModal(null)}>
            <DialogContent className="feature-detail-modal">
              <DialogHeader>
                <DialogTitle>{featureDetails[featureModal].title}</DialogTitle>
                <DialogDescription asChild>
                  <div className="feature-detail-content">
                    <p className="feature-description">{featureDetails[featureModal].description}</p>
                    
                    <div className="feature-section">
                      <h4>Key Features:</h4>
                      <ul className="feature-list">
                        {featureDetails[featureModal].features.map((feature, idx) => (
                          <li key={idx} dangerouslySetInnerHTML={{ __html: feature }} />
                        ))}
                      </ul>
                    </div>

                    <div className="cta-section">
                      <Button onClick={handleLogin} className="cta-button">
                        Join the Flock to Get Started! 🐑
                      </Button>
                    </div>
                  </div>
                </DialogDescription>
              </DialogHeader>
            </DialogContent>
          </Dialog>
        )}

        {/* Follow us */}
        <section className="follow-us" aria-label="Follow baad.tv on social media">
          <span className="follow-us-label">Follow us</span>
          <div className="follow-us-links">
            <a
              href="https://www.facebook.com/baadtv"
              target="_blank"
              rel="noopener noreferrer"
              className="follow-link"
              aria-label="Follow baad.tv on Facebook"
              data-testid="follow-facebook"
            >
              <Facebook size={18} />
              <span>Facebook</span>
            </a>
            <a
              href="https://www.instagram.com/baarry_at_baadtv/"
              target="_blank"
              rel="noopener noreferrer"
              className="follow-link"
              aria-label="Follow baarry_at_baadtv on Instagram"
              data-testid="follow-instagram"
            >
              <Instagram size={18} />
              <span>Instagram</span>
            </a>
          </div>
        </section>

        {/* Footer */}
        <footer className="landing-footer">
          <div className="footer-content">
            <p className="copyright">© {new Date().getFullYear()} Red Pen Software. All rights reserved.</p>
            
            <div className="footer-links">
              <Dialog open={disclaimerOpen} onOpenChange={setDisclaimerOpen}>
                <DialogTrigger asChild>
                  <button className="footer-link">Disclaimer</button>
                </DialogTrigger>
                <DialogContent className="disclaimer-dialog">
                  <DialogHeader>
                    <DialogTitle>AI-Powered Development</DialogTitle>
                    <DialogDescription asChild>
                      <div className="dialog-text">
                        <p>
                          <strong>Disclaimer:</strong> Baad.tv was built with the assistance of 
                          artificial intelligence technology. While we strive for the highest 
                          quality and reliability, please note that AI-assisted development was 
                          used in creating this application.
                        </p>
                        <p style={{ marginTop: '1rem' }}>
                          We continuously work to improve the platform and appreciate your feedback 
                          as we enhance the user experience.
                        </p>
                      </div>
                    </DialogDescription>
                  </DialogHeader>
                </DialogContent>
              </Dialog>

              <span className="footer-separator">•</span>

              <Dialog open={testingOpen} onOpenChange={setTestingOpen}>
                <DialogTrigger asChild>
                  <button className="footer-link">Under Testing...</button>
                </DialogTrigger>
                <DialogContent className="testing-dialog">
                  <DialogHeader>
                    <DialogTitle>Testing Notice</DialogTitle>
                    <DialogDescription asChild>
                      <div className="dialog-text">
                        <p>
                          <strong>Important:</strong> This application is currently undergoing active 
                          testing and development. Please be aware that:
                        </p>
                        <ul style={{ marginTop: '1rem', marginLeft: '1.5rem', listStyle: 'disc' }}>
                          <li>Features may change or be updated without notice</li>
                          <li>Data you enter may not be permanent and could be reset during testing phases</li>
                          <li>You may experience bugs or unexpected behavior</li>
                        </ul>
                        <p style={{ marginTop: '1rem' }}>
                          <strong>You are welcome to use the app</strong> and explore its features! 
                          Your feedback during this testing period is invaluable and helps us improve 
                          the platform.
                        </p>
                        <p style={{ marginTop: '1rem', color: '#E62F88', fontWeight: '600' }}>
                          Thank you for being an early tester! 🐑
                        </p>
                      </div>
                    </DialogDescription>
                  </DialogHeader>
                </DialogContent>
              </Dialog>

              <span className="footer-separator">•</span>

              <Dialog open={privacyOpen} onOpenChange={setPrivacyOpen}>
                <DialogTrigger asChild>
                  <button className="footer-link">Privacy Policy</button>
                </DialogTrigger>
                <DialogContent className="privacy-dialog">
                  <DialogHeader>
                    <DialogTitle>Privacy Policy</DialogTitle>
                    <DialogDescription asChild>
                      <div className="dialog-text">
                        <p style={{ marginBottom: '1rem' }}>
                          <strong>Last Updated:</strong> December 2024
                        </p>
                        
                        <h3 style={{ color: '#ffffff', fontSize: '1.1rem', marginTop: '1.5rem', marginBottom: '0.5rem' }}>
                          Information We Collect
                        </h3>
                        <p>
                          When you use Baad.tv, we collect information provided through Google authentication, 
                          including your name, email address, and profile picture. We also collect information 
                          about the TV shows you track, your ratings, reviews, and interactions with other users.
                        </p>

                        <h3 style={{ color: '#ffffff', fontSize: '1.1rem', marginTop: '1.5rem', marginBottom: '0.5rem' }}>
                          How We Use Your Information
                        </h3>
                        <p>
                          We use your information to provide and improve the Baad.tv service, including 
                          personalising your experience, connecting you with friends, and displaying relevant 
                          show recommendations and statistics.
                        </p>

                        <h3 style={{ color: '#ffffff', fontSize: '1.1rem', marginTop: '1.5rem', marginBottom: '0.5rem' }}>
                          Data Sharing
                        </h3>
                        <p>
                          Your reviews and ratings can be shared with friends you follow on the platform. 
                          We do not sell your personal information to third parties. During testing, data 
                          may be reset or removed as outlined in our Testing Notice.
                        </p>

                        <h3 style={{ color: '#ffffff', fontSize: '1.1rem', marginTop: '1.5rem', marginBottom: '0.5rem' }}>
                          Data Security
                        </h3>
                        <p>
                          We implement security measures to protect your information. However, as this is 
                          a testing application, please be aware that data persistence is not guaranteed.
                        </p>

                        <h3 style={{ color: '#ffffff', fontSize: '1.1rem', marginTop: '1.5rem', marginBottom: '0.5rem' }}>
                          Your Rights
                        </h3>
                        <p>
                          You have the right to access, update, or delete your personal information at any time 
                          through your profile settings.
                        </p>

                        <p style={{ marginTop: '1.5rem', fontSize: '0.9rem', color: '#999999' }}>
                          For questions about this Privacy Policy, please contact Red Pen Software.
                        </p>
                      </div>
                    </DialogDescription>
                  </DialogHeader>
                </DialogContent>
              </Dialog>

              <span className="footer-separator">•</span>

              <button 
                className="footer-link" 
                onClick={() => navigate('/trending')}
                data-testid="footer-trending-link"
              >
                Trending TV
              </button>

              <span className="footer-separator">•</span>

              <button 
                className="footer-link" 
                onClick={() => navigate('/faq')}
              >
                FAQ
              </button>
            </div>
          </div>
        </footer>
      </div>

      <style jsx>{`
        .landing-page {
          min-height: 100vh;
          background: #121212;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 2rem;
        }

        .landing-content {
          max-width: 1200px;
          width: 100%;
        }

        .landing-hero {
          text-align: center;
          margin-bottom: 4rem;
        }

        .logo-container {
          display: flex;
          justify-content: center;
          align-items: center;
          margin-bottom: 2rem;
        }

        .landing-logo {
          width: 150px;
          height: 150px;
          object-fit: contain;
          display: block;
          animation: float 3s ease-in-out infinite;
        }

        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }

        .landing-hero h1 {
          font-size: 4rem;
          font-weight: 700;
          color: #E62F88;
          margin-bottom: 0.5rem;
          line-height: 1.2;
          text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.5);
        }

        .tagline {
          font-size: 1.5rem;
          color: #E62F88;
          font-weight: 600;
          margin-bottom: 1rem;
          font-style: italic;
        }

        .landing-hero p {
          font-size: 1.25rem;
          color: #b0b0b0;
          margin-bottom: 2.5rem;
          max-width: 650px;
          margin-left: auto;
          margin-right: auto;
          line-height: 1.6;
        }

        .login-btn {
          font-size: 1.1rem;
          padding: 1.25rem 2.5rem;
          background: #E62F88;
          color: #000000;
          border-radius: 8px;
          font-weight: 700;
          display: inline-flex;
          align-items: center;
          gap: 0.75rem;
          transition: background 0.2s;
          border: none;
        }

        .login-btn:hover {
          background: #E48BB6;
          color: #000000;
        }

        .google-icon {
          width: 20px;
          height: 20px;
        }

        .features-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
          gap: 2rem;
          max-width: 1100px;
          margin: 0 auto;
        }
        @media (min-width: 900px) {
          .features-grid {
            grid-template-columns: repeat(3, 1fr);
          }
        }

        .feature-card {
          background: #1a1a1a;
          padding: 2rem;
          border-radius: 12px;
          border: 1px solid #333333;
          transition: transform 0.2s, border-color 0.2s;
          position: relative;
        }

        .clickable-card {
          cursor: pointer;
        }

        .clickable-card:hover {
          transform: translateY(-4px);
          border-color: #E62F88;
        }

        .click-hint {
          margin-top: 1rem;
          color: #E62F88;
          font-size: 0.85rem;
          font-weight: 600;
          opacity: 0;
          transition: opacity 0.2s;
        }

        .clickable-card:hover .click-hint {
          opacity: 1;
        }

        .feature-icon {
          font-size: 3rem;
          margin-bottom: 1rem;
          display: flex;
          justify-content: center;
          align-items: center;
          min-height: 80px;
        }

        .icon-image {
          width: 80px;
          height: 80px;
          object-fit: contain;
        }

        .feature-card h3 {
          color: #ffffff;
          font-size: 1.5rem;
          font-weight: 600;
          margin-bottom: 0.75rem;
        }

        .feature-card p {
          color: #b0b0b0;
          font-size: 1rem;
          line-height: 1.5;
        }

        /* ---------- Trending promo strip ---------- */
        .trending-promo {
          margin-top: 4rem;
          position: relative;
        }
        .trending-promo-inner {
          position: relative;
          overflow: hidden;
          background:
            radial-gradient(
              900px 320px at 110% -10%,
              rgba(230, 47, 136, 0.22) 0%,
              rgba(230, 47, 136, 0) 60%
            ),
            linear-gradient(135deg, #15090f 0%, #0d0d0d 100%);
          border: 1px solid #2a1118;
          border-radius: 18px;
          padding: 2.25rem 2.25rem 2.5rem;
          display: grid;
          grid-template-columns: 1fr;
          gap: 2rem;
          align-items: center;
        }
        @media (min-width: 900px) {
          .trending-promo-inner {
            grid-template-columns: minmax(0, 1.5fr) minmax(280px, 1fr);
            gap: 2.5rem;
          }
        }
        .trending-promo-inner::before {
          content: '';
          position: absolute;
          inset: -1px;
          border-radius: 18px;
          padding: 1px;
          background: linear-gradient(135deg, rgba(230, 47, 136, 0.55), transparent 65%);
          -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
          -webkit-mask-composite: xor;
                  mask-composite: exclude;
          pointer-events: none;
        }
        .trending-promo-content {
          display: flex;
          flex-direction: column;
          gap: 0.55rem;
          max-width: 720px;
        }
        .trending-promo-eyebrow {
          color: #E62F88;
          font-size: 0.7rem;
          letter-spacing: 0.22em;
          text-transform: uppercase;
          font-weight: 800;
        }
        .trending-promo-content h2 {
          color: #fff;
          font-size: clamp(1.5rem, 2.6vw, 1.95rem);
          font-weight: 800;
          letter-spacing: -0.01em;
          margin: 0;
          line-height: 1.15;
        }
        .trending-promo-content p {
          color: #c4c4cc;
          font-size: 1rem;
          line-height: 1.6;
          margin: 0 0 0.85rem;
          max-width: 640px;
        }
        .trending-promo-cta {
          align-self: flex-start;
          background: #E62F88;
          color: #000;
          border: none;
          padding: 0.85rem 1.5rem;
          border-radius: 8px;
          font-weight: 700;
          font-size: 0.95rem;
          cursor: pointer;
          transition: background 0.18s, transform 0.18s;
        }
        .trending-promo-cta:hover {
          background: #ff4f9e;
          transform: translateY(-1px);
        }
        @media (max-width: 720px) {
          .trending-promo {
            margin-top: 2.5rem;
          }
          .trending-promo-inner {
            padding: 1.5rem;
            gap: 1.5rem;
          }
        }

        .follow-us {
          margin-top: 3.5rem;
          padding: 1.25rem 0 0;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.75rem;
          border-top: 1px solid #1f1f1f;
        }
        .follow-us-label {
          color: #777;
          font-size: 0.85rem;
          letter-spacing: 0.08em;
          text-transform: uppercase;
        }
        .follow-us-links {
          display: flex;
          gap: 0.75rem;
          flex-wrap: wrap;
          justify-content: center;
        }
        .follow-link {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.55rem 1rem;
          border-radius: 999px;
          background: #1a1a1a;
          border: 1px solid #2a2a2a;
          color: #ddd;
          font-size: 0.85rem;
          font-weight: 500;
          text-decoration: none;
          transition: border-color 0.18s, color 0.18s, transform 0.18s, background 0.18s;
        }
        .follow-link:hover {
          color: #fff;
          border-color: #E62F88;
          background: rgba(230, 47, 136, 0.12);
          transform: translateY(-1px);
        }
        .follow-link:focus-visible {
          outline: 2px solid #E62F88;
          outline-offset: 2px;
        }

        .landing-footer {
          margin-top: 4rem;
          padding-top: 2rem;
          border-top: 1px solid #333333;
        }

        .footer-content {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 1rem;
        }

        .copyright {
          color: #b0b0b0;
          font-size: 0.9rem;
          margin: 0;
        }

        .footer-links {
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }

        .footer-link {
          background: none;
          border: none;
          color: #E62F88;
          font-size: 0.9rem;
          cursor: pointer;
          padding: 0;
          transition: color 0.2s;
          text-decoration: underline;
        }

        .footer-link:hover {
          color: #E48BB6;
        }

        .footer-separator {
          color: #666666;
          font-size: 0.9rem;
        }

        .dialog-text {
          color: #b0b0b0;
          line-height: 1.7;
          font-size: 0.95rem;
        }

        .dialog-text p {
          margin: 0;
        }

        .dialog-text strong {
          color: #ffffff;
        }

        .dialog-text li {
          margin-bottom: 0.5rem;
          color: #b0b0b0;
        }

        .feature-detail-modal {
          max-width: 600px;
        }

        .feature-detail-content {
          color: #b0b0b0;
        }

        .feature-description {
          font-size: 1rem;
          line-height: 1.7;
          margin-bottom: 1.5rem;
          color: #ffffff;
        }

        .feature-section {
          margin-bottom: 1.5rem;
        }

        .feature-section h4 {
          color: #E62F88;
          font-size: 1.1rem;
          margin-bottom: 0.75rem;
        }

        .feature-list {
          list-style: none;
          padding-left: 0;
        }

        .feature-list li {
          padding-left: 1.5rem;
          margin-bottom: 0.75rem;
          position: relative;
          line-height: 1.6;
        }

        .feature-list li:before {
          content: "🐑";
          position: absolute;
          left: 0;
        }

        .feature-list strong {
          color: #ffffff;
        }

        .cta-section {
          margin-top: 2rem;
          padding-top: 1.5rem;
          border-top: 1px solid #333333;
          text-align: center;
        }

        .cta-button {
          background: #E62F88 !important;
          color: #000000 !important;
          padding: 0.75rem 2rem !important;
          font-size: 1rem !important;
          font-weight: 600 !important;
        }

        .cta-button:hover {
          background: #c92570 !important;
        }

        @media (max-width: 768px) {
          .landing-page {
            padding: 1rem;
          }

          .landing-hero {
            margin-bottom: 2rem;
          }

          .landing-logo {
            width: 100px;
            height: 100px;
          }

          .landing-hero h1 {
            font-size: 2.5rem;
            margin-bottom: 0.5rem;
          }

          .tagline {
            font-size: 1.1rem;
            margin-bottom: 0.75rem;
          }

          .landing-hero p {
            font-size: 1rem;
            margin-bottom: 1.5rem;
          }

          .login-btn {
            width: 100%;
            max-width: 300px;
            padding: 1rem 2rem;
            font-size: 1rem;
          }

          .features-grid {
            grid-template-columns: 1fr;
            gap: 1.5rem;
          }

          .feature-card {
            padding: 1.5rem;
          }

          .feature-card h3 {
            font-size: 1.25rem;
          }

          .landing-footer {
            margin-top: 2.5rem;
            padding-top: 1.5rem;
            padding-bottom: 80px;
          }

          .footer-content {
            gap: 0.75rem;
          }

          .copyright {
            font-size: 0.8rem;
            text-align: center;
          }

          .footer-links {
            flex-wrap: wrap;
            justify-content: center;
            gap: 0.5rem;
          }

          .footer-link {
            font-size: 0.85rem;
          }
        }
      `}</style>
    </div>
    </>
  );
};

export default Landing;